import { inject, Injectable } from '@angular/core';
import { FeatureFlagsService } from '@rms/core/feature-flags';
import { AgentOrganizationStoreService, ExternalPbxProvider, OrganizationStoreService, Sections } from '@rms/org';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { WhiteLabelService } from '../layout';
import {
  NavigationCallback,
  NavigationDropdown,
  NavigationItem,
  NavigationLink,
  NavigationSubheading,
} from './navigation-item.interface';
import { NavigationLoaderService } from './navigation-loader.service';

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private readonly navigationLoaderService = inject(NavigationLoaderService);
  private readonly organizationStore = inject(OrganizationStoreService);
  private readonly agentOrganizationStore = inject(AgentOrganizationStoreService);
  private readonly whiteLabelService = inject(WhiteLabelService);
  private readonly featureFlagsService = inject(FeatureFlagsService);

  private readonly _openChangeSubject = new Subject<NavigationDropdown>();

  public items$ = this.navigationLoaderService.items$.pipe(
    map((navigationItems) => {
      return navigationItems.filter((item) => {
        if (item.type !== 'link') {
          return true;
        }

        if (item.featureFlag && !this.featureFlagsService.enabled(item.featureFlag)) {
          return false;
        }

        if (!item.section) {
          return true;
        }

        const isVoxloud = this.agentOrganizationStore.data?.pbx?.provider === ExternalPbxProvider.VOXLOUD;

        // hide PhoneSystem for voxloud PBX orgs
        if (item.section === Sections.PhoneSystem && isVoxloud) {
          return false;
        }

        // hide Shop for all voxloud orgs
        if (item.section === Sections.Shop && isVoxloud) {
          return false;
        }

        const allowedByWhitelabel = this.whiteLabelService.isMenuItemAvailable(item.section);
        const allowedByOrg = !item.restricted || this.organizationStore.embeddedApps.includes(item.section);

        return allowedByWhitelabel && allowedByOrg;
      });
    })
  );

  public openChange$ = this._openChangeSubject.asObservable();

  public triggerOpenChange(item: NavigationDropdown): void {
    this._openChangeSubject.next(item);
  }

  public isLink(item: NavigationItem): item is NavigationLink {
    return item.type === 'link';
  }

  public isDropdown(item: NavigationItem): item is NavigationDropdown {
    return item.type === 'dropdown';
  }

  public isSubheading(item: NavigationItem): item is NavigationSubheading {
    return item.type === 'subheading';
  }

  public isCallback(item: NavigationItem): item is NavigationCallback {
    return item.type === 'callback';
  }
}
